/**
 * Shared date rules for the dashboard report filters
 * (Idea, Challenge and Evaluator reports).
 *
 * All dates are YYYY-MM-DD strings, exactly as reported
 * by <input type="date">.
 */

export const MAX_DATE_RANGE_MONTHS = 12;

/*
 * Earliest date that can be selected or sent to the API.
 */
export const MIN_REPORT_DATE = "2020-01-01";
export const MIN_REPORT_DATE_LABEL = "01/01/2020";

/*
 * Strict YYYY-MM-DD. Guarantees a 4-digit year so that
 * plain string comparison of two dates is chronological.
 */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Convert a Date object to YYYY-MM-DD.
 */
export const formatDate = date => {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Convert YYYY-MM-DD to a local Date object.
 * Returns null for empty, malformed or impossible
 * dates (e.g. 2023-02-30).
 *
 * Local parsing avoids timezone date shifting, and
 * setFullYear avoids years 0-99 being mapped to 19xx.
 */
export const parseInputDate = value => {
  if (!value || !DATE_PATTERN.test(value)) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);

  const date = new Date(0);
  date.setFullYear(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

export const getTodayDate = () => formatDate(new Date());

/**
 * Add (or subtract) calendar months.
 *
 * Example:
 * 2024-02-29 + 12 months becomes 2025-02-28.
 */
export const addMonths = (dateValue, months) => {
  const date = parseInputDate(dateValue);

  if (!date) {
    return "";
  }

  const originalDay = date.getDate();

  /*
   * Move to the first day before changing the month
   * to avoid invalid month rollover.
   */
  date.setDate(1);
  date.setMonth(date.getMonth() + months);

  const lastDayOfTargetMonth = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0
  ).getDate();

  date.setDate(Math.min(originalDay, lastDayOfTargetMonth));

  return formatDate(date);
};

/**
 * Validate a single, non-empty date value.
 */
export const getDateValueError = (value, label, todayDate) => {
  if (!parseInputDate(value)) {
    return `Enter a valid ${label}.`;
  }

  if (value < MIN_REPORT_DATE) {
    return `${label} cannot be before ${MIN_REPORT_DATE_LABEL}.`;
  }

  if (value > todayDate) {
    return "Future dates are not allowed.";
  }

  return "";
};

export const isValidDateValue = (value, todayDate) =>
  Boolean(value) &&
  !getDateValueError(value, "Date", todayDate);

/**
 * End Date maximum is the earlier of:
 * 1. Start Date plus 12 calendar months
 * 2. Today's date
 */
export const getMaxEndDate = (startDate, todayDate) => {
  if (!isValidDateValue(startDate, todayDate)) {
    return todayDate;
  }

  const maxRangeDate = addMonths(
    startDate,
    MAX_DATE_RANGE_MONTHS
  );

  return maxRangeDate < todayDate ? maxRangeDate : todayDate;
};

/**
 * Validate the dates that have been entered,
 * ignoring missing values.
 */
export const getEnteredDatesError = (
  startDate,
  endDate,
  todayDate
) => {
  if (startDate) {
    const startDateError = getDateValueError(
      startDate,
      "Start Date",
      todayDate
    );

    if (startDateError) {
      return startDateError;
    }
  }

  if (endDate) {
    const endDateError = getDateValueError(
      endDate,
      "End Date",
      todayDate
    );

    if (endDateError) {
      return endDateError;
    }
  }

  if (startDate && endDate) {
    if (endDate < startDate) {
      return "End Date cannot be earlier than Start Date.";
    }

    if (endDate > addMonths(startDate, MAX_DATE_RANGE_MONTHS)) {
      return `Date range cannot be more than ${MAX_DATE_RANGE_MONTHS} months.`;
    }
  }

  return "";
};

/**
 * Single source of truth for a report date range.
 * Returns an error message, or "" when the range is valid
 * (or when optional dates are both empty).
 */
export const validateDateRange = (
  startDate,
  endDate,
  {
    isDateRequired = false,
    requiredMessage = "Start Date and End Date are required.",
    todayDate = getTodayDate()
  } = {}
) => {
  if (!startDate && !endDate) {
    return isDateRequired ? requiredMessage : "";
  }

  if (!endDate) {
    return "End Date is mandatory when Start Date is selected.";
  }

  if (!startDate) {
    return "Start Date is mandatory when End Date is selected.";
  }

  return getEnteredDatesError(startDate, endDate, todayDate);
};

/**
 * End Date to keep after Start Date editing has finished.
 *
 * Clears End Date only if Start Date actually changed to a
 * valid date and the existing End Date no longer fits.
 * Temporary/invalid Start Date values never clear End Date.
 */
export const getEndDateAfterStartChange = (
  previousStartDate,
  startDate,
  endDate,
  todayDate
) => {
  if (
    !endDate ||
    startDate === previousStartDate ||
    !isValidDateValue(startDate, todayDate)
  ) {
    return endDate;
  }

  return endDate >= startDate &&
    endDate <= getMaxEndDate(startDate, todayDate)
    ? endDate
    : "";
};
